import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuArrowLeft, LuArrowRight, LuRefreshCw } from "react-icons/lu";
import { HiExclamationTriangle } from "react-icons/hi2";
import AppModal from "../common/modal/AppModal";
import { authedFetch } from "../../../common/http_service";
import "./_activityLog.scss";

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

const PAGE_SIZE = 15;

/**
 * Credit activity for THIS training — the editor-side counterpart of the
 * console's "Aktivite Geçmişi" page.
 *
 * Reads the same endpoint the console uses, `GET /organizations/me/transactions`,
 * pinned to one `trainingId`. That endpoint resolves the organization from the
 * bearer token, so the editor needs nothing but the training it already has
 * open — no org id to plumb through, and no way to read another org's ledger.
 *
 * Deliberately read-only: this answers "what did this course cost me" while the
 * author is inside it, instead of making them leave for the console.
 */
const ActivityLogModal = ({ show, onClose, trainingId }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (targetPage) => {
      if (!trainingId) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          trainingId,
          page: String(targetPage),
          limit: String(PAGE_SIZE),
        });
        const res = await authedFetch(`${apiBase()}/api/organizations/me/transactions?${params}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number(data?.total) || 0);
        // `summary` covers the WHOLE filtered set, not just this page, so it
        // keeps showing the training's real total while paging.
        setSummary(data?.summary ?? null);
        setPage(Number(data?.page) || targetPage);
      } catch (err) {
        setError(
          i18n.t("activity_log.error", {
            defaultValue: "Kayıtlar yüklenemedi.",
          })
        );
        // eslint-disable-next-line no-console
        console.warn("[ActivityLogModal] fetch failed:", err);
      } finally {
        setLoading(false);
      }
    },
    [trainingId]
  );

  // Fetch on open, and start from page 1 each time — a stale page number from
  // a previous visit would show an unrelated slice of the ledger.
  useEffect(() => {
    if (show) load(1);
  }, [show, load]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fmtDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleString(i18n.language || "tr", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
  };
  const fmtAmount = (value) =>
    Number(value || 0).toLocaleString(i18n.language || "tr", {
      maximumFractionDigits: 2,
    });
  const personOf = (row) => {
    const name = [row?.user?.name, row?.user?.surname].filter(Boolean).join(" ");
    return name || row?.user?.email || "—";
  };
  // Prefer the job's own label — it says what was produced ("Ses oluşturma"),
  // whereas `reason` is often an internal code.
  const labelOf = (row) => row?.job?.name || row?.reason || row?.job?.type || "—";

  return (
    <AppModal
      show={show}
      onClose={onClose}
      size="lg"
      centered
      title={i18n.t("activity_log.title", { defaultValue: "Aktivite Geçmişi" })}
      className="pageModal activityLogModal"
    >
      <div className="activityLog">
        <div className="activityLog__head">
          <p className="activityLog__hint">
            {i18n.t("activity_log.hint", {
              defaultValue: "Bu eğitim için harcanan kredilerin dökümü.",
            })}
          </p>
          <button
            type="button"
            className="activityLog__refresh"
            onClick={() => load(page)}
            disabled={loading}
            title={i18n.t("activity_log.refresh", { defaultValue: "Yenile" })}
          >
            <LuRefreshCw size={13} className={loading ? "spin" : undefined} />
          </button>
        </div>

        {summary && (
          <div className="activityLog__summary">
            <div className="activityLog__stat">
              <span className="activityLog__statLabel">
                {i18n.t("activity_log.total_spent", {
                  defaultValue: "Toplam harcanan",
                })}
              </span>
              <strong className="activityLog__statValue">
                {fmtAmount(summary.totalAmount)}{" "}
                {i18n.t("navbar.credits", { defaultValue: "kredi" })}
              </strong>
            </div>
            <div className="activityLog__stat">
              <span className="activityLog__statLabel">
                {i18n.t("activity_log.total_count", {
                  defaultValue: "İşlem sayısı",
                })}
              </span>
              <strong className="activityLog__statValue">{summary.totalCount}</strong>
            </div>
          </div>
        )}

        {error && (
          <div className="activityLog__error">
            <HiExclamationTriangle />
            <span>{error}</span>
            <button type="button" onClick={() => load(page)}>
              {i18n.t("activity_log.retry", { defaultValue: "Tekrar dene" })}
            </button>
          </div>
        )}

        {!error && !loading && items.length === 0 && (
          <div className="activityLog__empty">
            {i18n.t("activity_log.empty", {
              defaultValue: "Bu eğitim için henüz bir kredi hareketi yok.",
            })}
          </div>
        )}

        {items.length > 0 && (
          <div className="activityLog__tableWrap">
            <table className="activityLog__table">
              <thead>
                <tr>
                  <th>{i18n.t("activity_log.col_date", { defaultValue: "Tarih" })}</th>
                  <th>
                    {i18n.t("activity_log.col_operation", {
                      defaultValue: "İşlem",
                    })}
                  </th>
                  <th>{i18n.t("activity_log.col_user", { defaultValue: "Kullanıcı" })}</th>
                  <th className="activityLog__num">
                    {i18n.t("activity_log.col_amount", { defaultValue: "Kredi" })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const isCredit = row.type === "credit";
                  return (
                    <tr key={row.id}>
                      <td className="activityLog__date">{fmtDate(row.createdAt)}</td>
                      <td>
                        <span className="activityLog__op">{labelOf(row)}</span>
                        {row.job?.status && (
                          <span className="activityLog__jobStatus">{row.job.status}</span>
                        )}
                      </td>
                      <td className="activityLog__user">{personOf(row)}</td>
                      <td
                        className={`activityLog__num activityLog__amount${
                          isCredit ? " is-credit" : " is-debit"
                        }`}
                      >
                        {isCredit ? "+" : "−"}
                        {fmtAmount(Math.abs(row.amount))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {lastPage > 1 && (
          <div className="activityLog__pager">
            <button type="button" disabled={loading || page <= 1} onClick={() => load(page - 1)}>
              <LuArrowLeft size={13} />
            </button>
            <span>
              {page} / {lastPage}
            </span>
            <button
              type="button"
              disabled={loading || page >= lastPage}
              onClick={() => load(page + 1)}
            >
              <LuArrowRight size={13} />
            </button>
          </div>
        )}
      </div>
    </AppModal>
  );
};

ActivityLogModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** IdealStudioTraining UUID whose ledger to show. */
  trainingId: PropTypes.string,
};

export default ActivityLogModal;
